<template>
  <div class="tile-grid-wrapper">
    <div class="container">
      <div
        :class="['tile-grid', { 'no-border': !border, half: half }]"
        role="list"
      >
        <NuxtLink
          v-for="(item, index) in enrichedTiles"
          :key="index"
          :to="item.url"
          role="listitem"
          :aria-label="`Go to ${item.name} page`"
          class="tile-link"
          :class="{
            sqr: item.col === 4,
            rect: item.col === 8,
            full: item.col === 12,
          }"
        >
          <NuxtImg
            v-if="item.image_url"
            :src="item.image_url + '?auto=format,compress&fm=webp'"
            format="webp"
            :alt="`Illustration for ${item.name}`"
            class="tile-image"
          />
          <div class="tile-overlay-text">
            {{ item.name }}
          </div>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script setup>
import _find from "lodash/find";
import _filter from "lodash/filter";
import { useMainStore } from "~/store";
const store = useMainStore();

const props = defineProps(["featuredTiles", "border", "half"]);

const enrichedTiles = computed(() => {
  const galleries = store.galleryData || [];
  const menuGallery = _find(galleries, (g) => g.name === "Menu Options");
  const menuItems = menuGallery?.gallery_items || [];

  const result = [];

  for (const tile of props.featuredTiles) {
    const imageKey = tile.imageKey || tile.name;

    const matchedGalleryItems = _filter(menuItems, (g) => g.name === imageKey);
    if (matchedGalleryItems.length) {
      for (const galleryItem of matchedGalleryItems) {
        result.push({
          name: galleryItem.name,
          url: galleryItem.url || "#",
          col: tile.col || 4,
          image_url:
            tile.col === 4 && galleryItem.image_url_xs
              ? galleryItem.image_url_xs
              : (tile.col === 8 || 12) && galleryItem.image_url_lg
              ? galleryItem.image_url_lg
              : galleryItem.image_url,
        });
      }
    } else {
      result.push({
        name: tile.name,
        url: tile.url || "#",
        col: tile.col || 4,
        image_url: null,
      });
    }
  }
  return result;
});
</script>

<style lang="scss" scoped>
.tile-grid-wrapper {
  margin: 40px 0;

  .container {
    padding-right: 0px !important;
    padding-left: 0px !important;

    @media only screen and (max-width: 1024px) {
      padding-right: 60px !important;
      padding-left: 60px !important;
    }

    @media only screen and (max-width: 992px) {
      padding-right: 50px !important;
      padding-left: 50px !important;
    }

    @media only screen and (max-width: 580px) {
      padding-right: 20px !important;
      padding-left: 20px !important;
    }
  }

  @media (max-width: 580px) {
    padding: 0 0 0px;
    gap: 20px;
  }
}

.tile-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 50px;
  row-gap: 50px;
  padding-bottom: 40px;
  border-bottom: 8px solid $black;

  &.half {
    grid-template-columns: repeat(2, 1fr);
    padding-left: 50px;
    padding-right: 50px;

    @media (max-width: 1024px) {
      padding-left: 0px;
      padding-right: 0px;
      grid-template-columns: unset !important;
      column-gap: 50px !important;
      max-height: unset !important;
      height: auto !important;
    }
  }

  &.no-border {
    padding-bottom: 0px;
    border-bottom: none;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    padding: 0 0 40px;
    gap: 50px;
  }

  @media (max-width: 580px) {
    padding: 0 0 40px;
    gap: 20px;
  }
}

.tile-link {
  position: relative;
  display: block;
  overflow: hidden;
  width: 100%;
}

.tile-link.sqr {
  aspect-ratio: 1 / 1;
}

.tile-link.rect {
  grid-column: span 2;
  max-height: 377px;

  @media (max-width: 1024px) {
    grid-column: span 1 !important;
    aspect-ratio: 1 / 1 !important;
    max-height: unset !important;
    height: auto !important;
  }
}

@media (max-width: 1024px) {
  .tile-link {
    aspect-ratio: 1 / 1 !important;
    grid-column: span 1 !important;
  }
}

.tile-link.full {
  grid-column: span 3;
  aspect-ratio: 3 / 1;

  @media (max-width: 1024px) {
    grid-column: span 1 !important;
    aspect-ratio: 1 / 1 !important;
    max-height: unset !important;
    height: auto !important;
  }
}

.tile-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.tile-overlay-text {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $white;
  background-color: rgba(0, 0, 0, 0.25);
  padding: 10px 30px;
  text-align: center;
  box-sizing: border-box;
  text-transform: uppercase;
  font-size: 33px;
  font-weight: 300;
  letter-spacing: 2px;
  text-shadow: 2px 2px 3px rgba(0, 0, 0, 0.5);
  font-family: $font_poppin;
  line-height: 38px;
  pointer-events: none;
  transition: background-color 0.3s ease;
}

.tile-link:hover .tile-overlay-text {
  background-color: rgba(0, 0, 0, 0.15);
}
</style>